import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useSkipsData } from '../context/SkipsDataContext';
import { useResponsive } from '../hooks/useResponsive';
import Settings from './Settings';
import { WASTE_TYPES } from '../constants/jobOptions';

const FilterSidebar = ({ view = 'map', isOpen, onToggle }) => {
    const viewport = useResponsive();
    const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
    const paymentDropdownRef = useRef(null);
    const navigate = useNavigate();
    const {
        jobs,
        filters,
        setFilters,
        timeSettings,
        saveTimeSettings,
        getUniqueDriversForMap,
        getUniqueDriversForRecords,
        getFilteredJobsForMap,
        getFilteredJobsForRecords,
        resetFilters
    } = useSkipsData();

    // Use view-specific functions
    const getUniqueDrivers = view === 'map' ? getUniqueDriversForMap : getUniqueDriversForRecords;
    const getFilteredJobs = view === 'map' ? getFilteredJobsForMap : getFilteredJobsForRecords;

    const skipSizes = [
        '2yd', '4yd', '6yd', '8yd', '8yd lock', '10yd', '12yd', '12yd lock', '14yd', '16yd', '16yd lock', '20yd', '40yd'
    ];

    const handleLogout = async () => {
        try {
            await signOut(auth);
            localStorage.removeItem('companyId');
            localStorage.removeItem('companyName');
            navigate('/');
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Memoize filtered jobs to avoid recalculation on every render
    const filteredJobs = useMemo(() => {
        return getFilteredJobs();
    }, [getFilteredJobs]);

    // Total jobs count based on view (unfiltered baseline)
    const totalJobs = useMemo(() => {
        if (view === 'map') {
            return jobs.filter(job => job.status?.toLowerCase() === 'delivered').length;
        }
        return jobs.filter(job => ['delivered', 'collected'].includes(job.status?.toLowerCase())).length;
    }, [jobs, view]);

    // Handle click outside to close payment dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target)) {
                setPaymentDropdownOpen(false);
            }
        };

        if (paymentDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [paymentDropdownOpen]);

    return (
        <>
            {/* Floating toggle button - only visible when sidebar is closed */}
            {!isOpen && (
                <button
                    style={{
                        position: 'absolute',
                        left: '10px',
                        top: '60px',
                        zIndex: 1001,
                        padding: '8px 16px',
                        backgroundColor: '#166534',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                    onClick={onToggle}
                >
                    Filters
                </button>
            )}

            {/* Mobile backdrop - visible when sidebar is open on mobile */}
            {viewport.isMobile && isOpen && (
                <div
                    onClick={onToggle}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 999,
                        transition: 'opacity 0.3s ease-in-out'
                    }}
                />
            )}

            {/* Sidebar */}
            <div style={{
                width: isOpen
                    ? (viewport.isMobile ? '85vw' : '280px')
                    : '0px',
                maxWidth: viewport.isMobile ? '320px' : 'none',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(28, 25, 23, 0.06)',
                borderRight: '1px solid #E7E5E4',
                padding: isOpen ? '20px' : '0',
                zIndex: 1000,
                position: viewport.isMobile ? 'fixed' : 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                transition: 'all 0.3s ease-in-out',
                overflow: isOpen ? 'auto' : 'hidden',
                display: 'flex',
                flexDirection: 'column',
                marginTop: '50px'
            }}>
                <div style={{ marginBottom: '20px', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#292524' }}>Filters</h2>
                            <p style={{ fontSize: '12px', color: '#78716C', marginTop: '4px' }}>
                                {filteredJobs.length} selected · {totalJobs} total
                            </p>
                        </div>
                        <button
                            style={{
                                padding: '6px 12px',
                                backgroundColor: '#166534',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                marginLeft: '10px'
                            }}
                            onClick={onToggle}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#14532D'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#166534'}
                        >
                            Hide
                        </button>
                    </div>
                </div>

                {/* Hire Days Filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Hire Days
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        value={filters.dateRange}
                        onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                    >
                        <option value="all">All</option>
                        <option value="recent">Recent (≤ {timeSettings.green} days)</option>
                        <option value="medium">Medium ({timeSettings.green+1}-{timeSettings.yellow} days)</option>
                        <option value="old">Old (>{timeSettings.yellow} days)</option>
                    </select>
                </div>

                {/* Payment Status Filter - Multi-select */}
                <div ref={paymentDropdownRef} style={{ marginBottom: '20px', position: 'relative' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Payment
                    </label>
                    {/* Custom dropdown styled to look exactly like native select */}
                    <div
                        onClick={() => setPaymentDropdownOpen(!paymentDropdownOpen)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#e5e7eb',
                            fontSize: '13.3333px', // Match browser default
                            fontFamily: 'inherit',
                            lineHeight: 'normal',
                            boxSizing: 'border-box',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            paddingRight: '30px',
                            backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 8px center',
                            backgroundSize: '16px',
                            appearance: 'none'
                        }}
                    >
                        <span style={{ color: '#000' }}>
                            {filters.paymentStatus.length === 4
                                ? 'All Payment Statuses'
                                : filters.paymentStatus.length === 0
                                ? 'None selected'
                                : `${filters.paymentStatus.length} selected`}
                        </span>
                    </div>

                    {/* Dropdown menu */}
                    {paymentDropdownOpen && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderTop: 'none',
                            borderRadius: '0 0 4px 4px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            marginTop: '-1px',
                            maxHeight: '200px',
                            overflowY: 'auto'
                        }}>
                            {/* Checkboxes */}
                            {['Paid', 'TO PAY', 'Account', 'Unknown'].map((status) => (
                                <label
                                    key={status}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: filters.paymentStatus.includes(status) ? '#EFF6FF' : 'white',
                                        borderBottom: '1px solid #F3F4F6'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!filters.paymentStatus.includes(status)) {
                                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!filters.paymentStatus.includes(status)) {
                                            e.currentTarget.style.backgroundColor = 'white';
                                        }
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={filters.paymentStatus.includes(status)}
                                        onChange={(e) => {
                                            const newPaymentStatus = e.target.checked
                                                ? [...filters.paymentStatus, status]
                                                : filters.paymentStatus.filter(s => s !== status);
                                            setFilters({ ...filters, paymentStatus: newPaymentStatus });
                                        }}
                                        style={{
                                            marginRight: '8px',
                                            cursor: 'pointer',
                                            width: '16px',
                                            height: '16px'
                                        }}
                                    />
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: status === 'TO PAY' ? 'bold' : 'normal',
                                        color: status === 'TO PAY' ? '#EF4444' : '#374151'
                                    }}>
                                        {status}
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Invoiced Filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Invoiced
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        value={filters.invoiced}
                        onChange={(e) => setFilters({ ...filters, invoiced: e.target.value })}
                    >
                        <option value="all">All</option>
                        <option value="yes">Invoiced</option>
                        <option value="no">Not Invoiced</option>
                    </select>
                </div>

                {/* Skip Size Filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Size
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        value={filters.size}
                        onChange={(e) => setFilters({ ...filters, size: e.target.value })}
                    >
                        <option value="">All Sizes</option>
                        {skipSizes.map((size, index) => (
                            <option key={index} value={size}>{size}</option>
                        ))}
                    </select>
                </div>

                {/* Delivery Driver Filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Driver
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        value={filters.driver}
                        onChange={(e) => setFilters({ ...filters, driver: e.target.value })}
                    >
                        <option value="">All Drivers</option>
                        {getUniqueDrivers().map((driverName, index) => (
                            <option key={index} value={driverName}>{driverName}</option>
                        ))}
                    </select>
                </div>

                {/* Waste Type Filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Waste
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        value={filters.wasteType}
                        onChange={(e) => setFilters({ ...filters, wasteType: e.target.value })}
                    >
                        <option value="">All</option>
                        {WASTE_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>

                {/* Status Filter - Only visible in Records view */}
                {view === 'records' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}>
                            Status
                        </label>
                        <select
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                            }}
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <option value="all">All Statuses</option>
                            <option value="delivered">Delivered Only</option>
                            <option value="collected">Collected Only</option>
                        </select>
                    </div>
                )}

                {/* Settings Component - Only visible in Map view */}
                {view === 'map' && (
                    <Settings
                        timeSettings={timeSettings}
                        onSaveTimeSettings={saveTimeSettings}
                    />
                )}

                {/* Reset Button */}
                <div style={{ marginTop: 'auto', marginBottom: '20px' }}>
                    <button
                        onClick={resetFilters}
                        style={{
                            width: '100%',
                            padding: '8px 16px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#374151',
                            transition: 'all 0.2s ease-in-out'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    >
                        Clear
                    </button>
                </div>

                {/* Logout Button */}
                <div style={{
                    marginTop: '10px',
                    borderTop: '1px solid #E7E5E4',
                    paddingTop: '20px',
                    marginBottom: '20px'
                }}>
                    <button
                        onClick={handleLogout}
                        style={{
                            width: '100%',
                            padding: '8px 16px',
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B',
                            border: '1px solid #FECACA',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#DC2626';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#FEE2E2';
                            e.currentTarget.style.color = '#991B1B';
                        }}
                    >
                        Logout
                    </button>
                </div>
            </div>

            {/* Filter indicator overlay - only visible when sidebar is hidden and on desktop */}
            {!isOpen && !viewport.isMobile && (() => {
                // Build list of active filters
                const activeFilters = [];
                if (filters.dateRange !== 'all') {
                    const label = filters.dateRange === 'recent' ? `≤${timeSettings.green} days` :
                        filters.dateRange === 'medium' ? `${timeSettings.green+1}-${timeSettings.yellow} days` :
                        `>${timeSettings.yellow} days`;
                    activeFilters.push({ label: 'Hire', value: label });
                }
                if (filters.paymentStatus.length > 0 && filters.paymentStatus.length < 4) {
                    activeFilters.push({ label: 'Payment', value: filters.paymentStatus.join(', ') });
                }
                if (filters.size) {
                    activeFilters.push({ label: 'Size', value: filters.size });
                }
                if (filters.driver) {
                    activeFilters.push({ label: 'Driver', value: filters.driver });
                }
                if (filters.wasteType) {
                    activeFilters.push({ label: 'Waste', value: filters.wasteType });
                }
                if (filters.invoiced !== 'all') {
                    activeFilters.push({ label: 'Invoiced', value: filters.invoiced === 'yes' ? 'Yes' : 'No' });
                }
                if (view === 'records' && filters.status !== 'all') {
                    activeFilters.push({ label: 'Status', value: filters.status === 'delivered' ? 'Delivered' : 'Collected' });
                }

                return (
                    <div style={{
                        position: 'absolute',
                        left: '10px',
                        top: '110px',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        zIndex: 999,
                        boxShadow: '0 1px 3px rgba(28, 25, 23, 0.1)',
                        border: '1px solid #E7E5E4',
                        minWidth: '140px',
                        maxWidth: '200px'
                    }}>
                        <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#292524',
                            marginBottom: activeFilters.length > 0 ? '8px' : '0'
                        }}>
                            {filteredJobs.length} selected · {totalJobs} total
                        </div>
                        {activeFilters.length > 0 && (
                            <div style={{
                                borderTop: '1px solid #E7E5E4',
                                paddingTop: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                {activeFilters.map((filter, index) => (
                                    <div key={index} style={{
                                        fontSize: '12px',
                                        color: '#57534E',
                                        display: 'flex',
                                        gap: '4px'
                                    }}>
                                        <span style={{ color: '#78716C' }}>{filter.label}:</span>
                                        <span style={{ fontWeight: '500' }}>{filter.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })()}
        </>
    );
};

export default FilterSidebar;
